import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, User } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Users,
  Send,
  CheckCircle,
  BarChart3,
  ArrowLeftRight,
} from "lucide-react";
import { supabase } from "../lib/supabase";

export default function ParentPortal() {
  const navigate = useNavigate();
  const [children, setChildren] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkCode, setLinkCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkSuccess, setLinkSuccess] = useState("");

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      setLoading(true);
      const data = await api.getChildren();
      setChildren(data);
      // Note: Removed auto-redirect to allow parents to access portal features
      // even with a single child (e.g., comparison feature requires 2+ children)
    } catch (error) {
      console.error("Failed to load children:", error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to full analytics page when child is selected
  const selectChild = (child: User) => {
    navigate(`/student-analytics/${child.id}`);
  };

  const linkChild = async () => {
    setLinking(true);
    setLinkError("");
    setLinkSuccess("");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      const SUPABASE_URL =
        import.meta.env.VITE_SUPABASE_URL || "http://localhost:54321";
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/parent-send-link-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ child_username: linkCode.trim() }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send link request");
      }

      setLinkSuccess(data.message);
      setLinkCode("");
      // Don't reload children yet - link not approved
    } catch (error) {
      setLinkError(
        error instanceof Error ? error.message : "Failed to send link request",
      );
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Parent Portal
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Monitor your child's learning progress and achievements
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Children List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Your Children
              </CardTitle>
              {children.length >= 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/parent-portal/compare")}
                  className="flex items-center gap-2"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  Compare Children
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {children.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500 mb-2">No linked children yet</p>
                <p className="text-sm text-gray-400">
                  Use the form below to send a link request to your child
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 mb-4">
                  Click on a child to view their full analytics, including
                  practice history, mastery progress, and ranking.
                </p>
                {children.map((child) => (
                  <Button
                    key={child.id}
                    variant="outline"
                    className="w-full justify-between h-auto py-4 px-4"
                    onClick={() => selectChild(child)}
                  >
                    <span className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">{child.username}</span>
                    </span>
                    <span className="flex items-center gap-2 text-blue-600">
                      <BarChart3 className="w-4 h-4" />
                      View Analytics
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Link Request Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Link a Child
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">
              Enter your child's username to send a link request. They will need
              to approve the request before you can view their progress.
            </p>
            <div className="space-y-3">
              <Label htmlFor="linkCode">Child's Username</Label>
              <Input
                id="linkCode"
                placeholder="Enter child's username"
                value={linkCode}
                onChange={(e) => setLinkCode(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && !linking && linkCode && linkChild()
                }
              />
              {linkError && (
                <Alert variant="destructive">
                  <AlertDescription>{linkError}</AlertDescription>
                </Alert>
              )}
              {linkSuccess && (
                <Alert className="bg-green-50 text-green-900 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>{linkSuccess}</AlertDescription>
                </Alert>
              )}
              <Button
                className="w-full"
                onClick={linkChild}
                disabled={linking || !linkCode}
              >
                {linking ? (
                  "Sending Request..."
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Link Request
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
